import { useEffect, useMemo, useRef, useState } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { centerOfMass } from "@turf/turf"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { STATUS_CONFIG, PROPERTY_TYPE_CONFIG } from "@/lib/project-configs"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { SharedProps, PipeStatusRecord, ProjectStatus } from "@/types"
import type { IParcel, ICommune } from "@/types/explore"

interface CreateFolderDialogProps {
  open: boolean
  onClose: () => void
  selectedParcels: IParcel[]
  commune?: ICommune | null
  pipeStatuses: PipeStatusRecord[]
}

const PIPE_STATUS_GROUPS: { status: ProjectStatus; label: string }[] = [
  { status: "pending", label: STATUS_CONFIG.pending.label },
  { status: "accepted", label: STATUS_CONFIG.accepted.label },
  { status: "asset", label: STATUS_CONFIG.asset.label },
  { status: "rejected", label: STATUS_CONFIG.rejected.label },
  { status: "archived", label: STATUS_CONFIG.archived.label },
]

export const CreateFolderDialog = ({ open, onClose, selectedParcels, commune, pipeStatuses }: CreateFolderDialogProps) => {
  const { current_user } = usePage<SharedProps>().props
  const [pipeStatusOpen, setPipeStatusOpen] = useState(false)
  const pipeStatusRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!pipeStatusOpen) return
    const handleClick = (e: MouseEvent) => {
      if (pipeStatusRef.current && !pipeStatusRef.current.contains(e.target as Node)) {
        setPipeStatusOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [pipeStatusOpen])

  const prefill = useMemo(() => {
    if (selectedParcels.length === 0) return null
    const first = selectedParcels[0]
    const props = first.properties

    const center = centerOfMass(first.geometry).geometry.coordinates as [number, number]

    const totalSurface = selectedParcels.reduce((sum, p) => sum + (p.properties.contenance || 0), 0)

    const parcels = selectedParcels.map((p) => ({
      section: p.properties.section,
      number: p.properties.numero,
      prefix: p.properties.com_abs || "000",
    }))

    const communeName = commune?.nom || props.nom_com || ""
    const name =
      selectedParcels.length === 1
        ? `${props.section}${props.numero} - ${communeName}`
        : `${selectedParcels.length} parcelles - ${communeName}`

    // Sum building footprints as house_surface estimate
    const totalBuildingSurface = selectedParcels.reduce((sum, p) => {
      if (!p.buildings?.length) return sum
      return sum + p.buildings.reduce((s, b) => s + (b.surface_emprise_sol || 0), 0)
    }, 0)

    return {
      name,
      parcels,
      totalSurface,
      totalBuildingSurface,
      center,
      address: first.fullAddress || first.address || "",
      cityName: commune?.nom || props.nom_com || "",
      cityCode: commune?.code || props.code_insee || "",
      zipCode: commune?.codesPostaux?.[0] || first.zipCode || "",
    }
  }, [selectedParcels, commune])

  const defaultPipeStatusId = pipeStatuses.find((ps) => ps.parent_status === "pending")?.id || ""

  const { data, setData, post, processing, reset } = useForm({
    name: "",
    pipe_status_id: defaultPipeStatusId,
    property_type: "land",
    address: "",
    zip_code: "",
    city_name: "",
    city_code: "",
    field_surface: "",
    house_surface: "",
    latitude: 0,
    longitude: 0,
    origin: "creation",
    organisation_id: "",
    parcels: [] as { section: string; number: string; prefix: string }[],
  })

  useEffect(() => {
    if (open && prefill) {
      setData({
        name: prefill.name,
        pipe_status_id: defaultPipeStatusId,
        property_type: "land",
        address: prefill.address,
        zip_code: prefill.zipCode,
        city_name: prefill.cityName,
        city_code: prefill.cityCode,
        field_surface: String(prefill.totalSurface),
        house_surface: prefill.totalBuildingSurface ? String(Math.round(prefill.totalBuildingSurface)) : "",
        latitude: prefill.center[1],
        longitude: prefill.center[0],
        origin: "creation",
        organisation_id: String(current_user?.organisation_id || ""),
        parcels: prefill.parcels,
      })
    }
  }, [open, prefill])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/projects", {
      onSuccess: () => {
        onClose()
        reset()
      },
    })
  }

  const selectedPipeStatus = pipeStatuses.find((ps) => ps.id === data.pipe_status_id)

  // Group pipe statuses by parent status
  const pipeStatusesByParent: Record<string, PipeStatusRecord[]> = {}
  for (const ps of pipeStatuses) {
    if (!pipeStatusesByParent[ps.parent_status]) pipeStatusesByParent[ps.parent_status] = []
    pipeStatusesByParent[ps.parent_status].push(ps)
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Créer un dossier Kaptcher</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {selectedParcels.length} parcelle{selectedParcels.length > 1 ? "s" : ""} sélectionnée
            {selectedParcels.length > 1 ? "s" : ""}
            {prefill?.cityName ? ` · ${prefill.cityName}` : ""}
          </p>

          <div className="space-y-2">
            <Label htmlFor="folder_name">Nom du projet</Label>
            <Input id="folder_name" value={data.name} onChange={(e) => setData("name", e.target.value)} required />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Statut pipeline</Label>
              <div className="relative" ref={pipeStatusRef}>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                  onClick={() => setPipeStatusOpen(!pipeStatusOpen)}
                >
                  {selectedPipeStatus ? (
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block size-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: selectedPipeStatus.text_color }}
                      />
                      {selectedPipeStatus.label}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Statut...</span>
                  )}
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
                {pipeStatusOpen && (
                  <div className="absolute top-full left-0 z-50 mt-1 w-[250px] rounded-md border bg-popover shadow-md">
                    <Command>
                      <CommandInput placeholder="Rechercher..." />
                      <CommandList>
                        <CommandEmpty>Aucun résultat</CommandEmpty>
                        {PIPE_STATUS_GROUPS.map(({ status, label }) => {
                          const pipes = pipeStatusesByParent[status] || []
                          if (pipes.length === 0) return null
                          return (
                            <CommandGroup key={status} heading={label}>
                              {pipes.map((ps) => (
                                  <CommandItem
                                    key={ps.id}
                                    value={ps.label}
                                    onSelect={() => {
                                      setData("pipe_status_id", ps.id)
                                      setPipeStatusOpen(false)
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 size-4",
                                        data.pipe_status_id === ps.id ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span
                                      className="inline-block size-2.5 shrink-0 rounded-sm"
                                      style={{ backgroundColor: ps.text_color }}
                                    />
                                    {ps.label}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          )
                        })}
                      </CommandList>
                    </Command>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="folder_property_type">Type de bien</Label>
              <Select value={data.property_type} onValueChange={(v) => setData("property_type", v)}>
                <SelectTrigger>
                  <SelectValue>
                    {(() => {
                      const config = PROPERTY_TYPE_CONFIG[data.property_type]
                      if (!config) return null
                      const Icon = config.icon
                      return (
                        <span className="flex items-center gap-2">
                          <Icon className="size-4 text-muted-foreground" />
                          {config.label}
                        </span>
                      )
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROPERTY_TYPE_CONFIG).map(([value, config]) => {
                    const Icon = config.icon
                    return (
                      <SelectItem key={value} value={value}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-4 text-muted-foreground" />
                          {config.label}
                        </span>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="folder_address">Adresse</Label>
            <Input id="folder_address" value={data.address} onChange={(e) => setData("address", e.target.value)} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="folder_zip_code">Code postal</Label>
              <Input id="folder_zip_code" value={data.zip_code} onChange={(e) => setData("zip_code", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder_city_name">Ville</Label>
              <Input
                id="folder_city_name"
                value={data.city_name}
                onChange={(e) => setData("city_name", e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="folder_field_surface">Surface terrain (m²)</Label>
              <Input
                id="folder_field_surface"
                type="number"
                step="0.01"
                value={data.field_surface}
                onChange={(e) => setData("field_surface", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder_house_surface">Surface bâtie (m²)</Label>
              <Input
                id="folder_house_surface"
                type="number"
                step="0.01"
                value={data.house_surface}
                onChange={(e) => setData("house_surface", e.target.value)}
              />
            </div>
          </div>

          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              Créer le dossier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
